import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Calendar, Clock, Check, Sparkles, ChevronLeft, ShieldCheck,
  Users, Brain, Lock, Zap, FileText, MessageSquare, Award,
  Apple, Smartphone, Download, Bell, Loader2,
  Share2, Mail as MailIcon, Copy, Send, ExternalLink,
} from "lucide-react";

const ZOHO_APP_IOS = "https://apps.apple.com/us/app/zoho-webinar-online-webinars/id6443464550";
const ZOHO_APP_ANDROID = "https://play.google.com/store/apps/details?id=com.zoho.webinar";
const API = `${BACKEND_URL}/api`;
import { LOGO_URL } from "../lib/brand";
import { log } from "../lib/log";
import { BACKEND_URL } from "../lib/apiBase";
import { QAStickyButton } from "../components/QACommunity";

// The next-up regular webinar is fetched from the DB at runtime via
// /api/webinars/next (filtered to ``kind=regular`` — i.e. technik calls
// are excluded). We keep a fallback ISO so the page still renders in the
// (rare) case the API is unreachable; it intentionally points at a
// far-future timestamp so the countdown stays positive while the user
// retries / refreshes.
const FALLBACK_ISO = "2099-12-31T19:00:00Z";

function useCountdown(targetIso) {
  const target = useMemo(() => new Date(targetIso || FALLBACK_ISO).getTime(), [targetIso]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (24 * 3600 * 1000));
  const hours = Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000));
  const minutes = Math.floor((diff % (3600 * 1000)) / (60 * 1000));
  const seconds = Math.floor((diff % (60 * 1000)) / 1000);
  return { diff, days, hours, minutes, seconds };
}

// Formatters that reuse Europe/Zurich (Swiss time) regardless of visitor TZ.
const ZURICH = "Europe/Zurich";
const fmtDateLong = (iso) => iso ? new Date(iso).toLocaleDateString("de-CH", {
  weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: ZURICH,
}) : "";
const fmtDateShort = (iso) => iso ? new Date(iso).toLocaleDateString("de-CH", {
  day: "numeric", month: "long", timeZone: ZURICH,
}) : "";
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("de-CH", {
  hour: "2-digit", minute: "2-digit", hour12: false, timeZone: ZURICH,
}) : "";

// "sonntag, 31. mai 2026" → "Sonntag, 31. mai 2026". de-CH starts the
// weekday lowercase via toLocaleDateString — bump the first letter so
// the headline reads as a proper sentence.
const capitalizeFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const FACTS = [
  {
    icon: Award,
    title: "Du bist von Anfang an dabei",
    text: "Das System ist im echten Markt geprüft und startklar. Wir öffnen es jetzt für Privatanleger — du gehörst zu denen, die von Anfang an profitieren können.",
  },
  {
    icon: Brain,
    title: "KI + 4-Layer-Architektur",
    text: "Signal-, Risk-, Execution- und Monitoring-Engine arbeiten unabhängig — kein menschlicher Eingriff, keine Emotionen.",
  },
  {
    icon: ShieldCheck,
    title: "Dein Kapital, dein Konto",
    text: "Konto bleibt zu 100 % auf deinem Namen bei einem regulierten Broker. Wir haben nur Handelsausführungs-Vollmacht.",
  },
  {
    icon: Lock,
    title: "Hard-coded Risikolimits",
    text: "Daily-Loss-Limit, Drawdown-Kill-Switch und Korrelations-Cap sind im System fix verdrahtet — nicht verhandelbar.",
  },
  {
    icon: Zap,
    title: "Performance-Fee statt Fixkosten",
    text: "Wir verdienen nur, wenn du verdienst. Keine Setup-Gebühr, keine Monats-Pauschale, jederzeit kündbar.",
  },
  {
    icon: Users,
    title: "Aus dem Netzwerk gewachsen",
    text: "Über persönliche Empfehlungen — keine Werbung, kein Cold-Calling. Jeder Partner kennt jeden Partner.",
  },
];

const AGENDA = [
  { time: "00:00", item: "Begrüssung + warum jetzt der richtige Moment ist" },
  { time: "05:00", item: "Die Vision: Hedge-Fund-Strategien für Privatanleger" },
  { time: "15:00", item: "Wie das System wirklich tradet — die 4 Engines erklärt" },
  { time: "25:00", item: "Risiko-Management im Detail — was kann schiefgehen?" },
  { time: "35:00", item: "Onboarding: Wie kommst du in 30 Min live ans System" },
  { time: "45:00", item: "Live Q&A — frag alles, ehrlich" },
];

// "Refer a friend" share — pre-filled message body.
// Wir leiten den Empfänger zur Insider-Report-Landing, weil das die echte
// Conversion-Seite ist (Mail-Anmeldung + Drip-Sequenz). `?ref=share` markiert
// den Traffic in den Analytics, ohne ein dediziertes Partner-Programm zu nutzen.
const SHARE_URL = "https://alphalane-tradingsystems.com/insider-report?ref=share";

export default function WebinarThanks({ kind = "regular" } = {}) {
  // Page is reused under TWO routes:
  //   /webinar       (kind="regular")     → Pascal's customer-facing call
  //   /webinartech   (kind="technical")   → Adrian's setup/technik call
  //
  // The ``kind`` prop drives:
  //   * which webinar comes back from /api/webinars/next?kind=…
  //   * which webinar_id we bind WhatsApp opt-ins to
  //   * a few copy adjustments (eyebrow label + headline)
  const isTech = kind === "technical";
  const [webinar, setWebinar] = useState(null);
  const [webinarLoading, setWebinarLoading] = useState(true);

  const loadWebinar = useCallback(async () => {
    setWebinarLoading(true);
    try {
      const r = await axios.get(`${API}/webinars/next`, { params: { kind } });
      if (r.data?.ok && r.data?.webinar) {
        setWebinar(r.data.webinar);
      } else {
        setWebinar(null);
      }
    } catch (err) {
      log.warn("[webinar] /webinars/next fetch failed:", err);
      setWebinar(null);
    } finally {
      setWebinarLoading(false);
    }
  }, [kind]);

  useEffect(() => { loadWebinar(); }, [loadWebinar]);

  const targetIso = webinar?.scheduled_at || null;
  const webinarTitle = webinar?.title || (isTech ? "Alphalane Technik-Call" : "Alphalane Live-Webinar");
  const dateLong = fmtDateLong(targetIso);
  const dateShort = fmtDateShort(targetIso);
  const timeLabel = fmtTime(targetIso);

  // Share-text needs to update once the webinar is loaded — done via
  // useMemo so we don't recompute on every render but DO recompute when
  // the title/date change.
  const shareIntroDe = useMemo(() => (
    dateShort
      ? `Hey, am ${dateShort} zeigt Pascal sein Trading-System live — trag dich hier ein, das willst du nicht verpassen:`
      : "Hey, hier zeigt Pascal sein Trading-System live — trag dich ein, das willst du nicht verpassen:"
  ), [dateShort]);
  const shareTextDe = `${shareIntroDe} ${SHARE_URL}`;
  const shareSubject = dateShort
    ? `${webinarTitle} — ${dateShort}${timeLabel ? `, ${timeLabel}` : ""}`
    : webinarTitle;

  const { diff, days, hours, minutes, seconds } = useCountdown(targetIso);
  const isLive = !!targetIso && diff === 0;

  // WhatsApp reminder opt-in state
  const [waName, setWaName] = useState("");
  const [waCC, setWaCC] = useState("+41");
  const [waPhone, setWaPhone] = useState("");
  const [waSubmitting, setWaSubmitting] = useState(false);
  const [waError, setWaError] = useState("");
  const [waDone, setWaDone] = useState(false);

  // Share widget state
  const [copied, setCopied] = useState(false);
  const [canNativeShare] = useState(
    typeof navigator !== "undefined" && typeof navigator.share === "function"
  );

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      log.warn("[webinar] clipboard write blocked:", err);
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: shareSubject,
        text: shareTextDe,
        url: SHARE_URL,
      });
    } catch (err) {
      // user cancelled or share unsupported — debug only
      log.debug("[webinar] native share dismissed:", err);
    }
  };

  const submitReminder = async (e) => {
    e?.preventDefault?.();
    setWaError("");
    if (!waName.trim()) {
      setWaError("Bitte gib deinen Vornamen ein.");
      return;
    }
    const digits = (waPhone || "").replace(/\D+/g, "");
    if (digits.length < 6) {
      setWaError("Bitte gib deine WhatsApp-Nummer ein (ohne Ländercode).");
      return;
    }
    setWaSubmitting(true);
    try {
      await axios.post(`${API}/webinar/reminder`, {
        name: waName.trim(),
        phone: `${waCC}${digits}`,
        // Always bind opt-ins to the exact webinar shown on THIS page —
        // otherwise the /webinartech reminders would silently get logged
        // against the next /webinar (regular) event instead.
        webinar_id: webinar?.id || undefined,
      });
      setWaDone(true);
      // NB: we intentionally do NOT persist this in localStorage. The user
      // may register for multiple webinars over time (one Whatsapp opt-in
      // per webinar). Persisting would lock the form forever and prevent
      // re-opting for the next call. Duplicate de-dupe happens server-side
      // in /admin (manual merge tool).
    } catch (err) {
      const msg = err?.response?.data?.detail || "Da ist etwas schiefgegangen. Bitte versuch es nochmal.";
      setWaError(typeof msg === "string" ? msg : "Fehler beim Speichern.");
    } finally {
      setWaSubmitting(false);
    }
  };

  // Document title — recomputed once the dynamic webinar metadata arrives.
  useEffect(() => {
    document.title = dateShort
      ? `Du bist dabei — ${webinarTitle} · ${dateShort}`
      : `Du bist dabei — ${webinarTitle}`;
  }, [dateShort, webinarTitle]);

  useEffect(() => {
    // Mail-CTA support: ?name=Max&open_reminder=1#reminder
    // NB: we no longer auto-set `waDone=true` from localStorage on mount
    // — every page-load is a fresh opt-in opportunity so the user can
    // re-subscribe for the next webinar.
    try {
      const params = new URLSearchParams(window.location.search);
      const nameParam = params.get("name");
      if (nameParam) setWaName(nameParam.slice(0, 60));
      const open = params.get("open_reminder") === "1" || window.location.hash === "#reminder";
      if (open) {
        // Defer until layout settles, then smooth-scroll + focus the form.
        setTimeout(() => {
          const el = document.querySelector('[data-testid="webinar-whatsapp-reminder"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          const phone = document.querySelector('[data-testid="webinar-reminder-phone"]');
          if (phone) phone.focus({ preventScroll: true });
        }, 350);
      }
    } catch (err) {
      log.warn("[webinar] URL params parsing failed:", err);
    }
  }, []);

  // Calendar dropdown was removed (Mai 2026); CAL_TITLE/CAL_DESCRIPTION/calStart/calEnd
  // helpers also removed because they had no other consumers.

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="webinar-thanks-page">
      <header className="border-b border-[var(--al-line)] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-auto" />
            <span className="hidden sm:inline font-display font-bold text-base text-slate-900 group-hover:text-[var(--al-purple)] transition-colors">
              Alphalane
            </span>
          </Link>
          <Link
            to="/"
            data-testid="webinar-back-home"
            className="text-xs sm:text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" /> Zur Startseite
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        {/* Hero — confirmation */}
        <section className="text-center mb-14" data-testid="webinar-thanks-hero">
          <div className="inline-flex h-20 w-20 rounded-full al-grad-bg items-center justify-center shadow-2xl shadow-violet-200 mb-6">
            <Check className="h-10 w-10 text-white" strokeWidth={3} />
          </div>
          <span className="al-tag justify-self-center">
            <Sparkles className="h-3 w-3" /> Registrierung bestätigt
          </span>
          <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Du bist <span className="al-grad-text">dabei.</span>
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Danke für deine Anmeldung zum <strong className="text-slate-900">{webinarTitle}</strong>.
            Den Zugangslink bekommst du per E-Mail von Zoho zugeschickt — inklusive automatischer
            Erinnerungen vor dem Call. Bitte die App <strong className="text-slate-900">„Zoho Webinar“</strong> im
            Vorfeld herunterladen oder vom Browser am Computer teilnehmen.
          </p>
        </section>

        {/* Countdown */}
        <section className="mb-14 al-card" data-testid="webinar-countdown">
          <div className="bg-gradient-to-br from-slate-900 via-violet-900 to-indigo-900 p-8 sm:p-12 text-center text-white relative rounded-[18px]">
            <div className="absolute inset-0 opacity-30 pointer-events-none rounded-[18px]" style={{
              backgroundImage: "radial-gradient(circle at 78% 18%, rgba(124,58,237,0.6), transparent 55%), radial-gradient(circle at 12% 85%, rgba(74,141,240,0.45), transparent 50%)",
            }} />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-[10px] uppercase tracking-widest font-bold mb-3 text-violet-200">
                <Calendar className="h-3 w-3" /> Save the Date
              </div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl mb-1" data-testid="webinar-date">
                {webinarLoading ? "Termin wird geladen…" : (dateLong ? capitalizeFirst(dateLong) : "Bald wieder ein Live-Termin")}
              </h2>
              <p className="font-display text-violet-200 text-lg mb-8">
                {timeLabel ? `${timeLabel} Uhr · Schweizer Zeit · Online (Zoho Webinar)` : "Datum wird in Kürze bekanntgegeben"}
              </p>

              {!targetIso ? (
                <div className="py-8" data-testid="webinar-none">
                  <p className="text-violet-200">
                    Aktuell ist kein Live-Termin geplant. Du bist dennoch registriert — wir melden uns per E-Mail sobald der nächste Call steht.
                  </p>
                </div>
              ) : isLive ? (
                <div className="py-8" data-testid="webinar-live">
                  <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 text-white font-display font-bold text-xl shadow-lg animate-pulse">
                    <span className="h-2.5 w-2.5 rounded-full bg-white" /> LIVE JETZT
                  </div>
                  <p className="mt-4 text-violet-200">Check deine Mail für den Zoom-Link.</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2 sm:gap-4 max-w-2xl mx-auto" data-testid="countdown-digits">
                  <TimeBlock value={days} label="Tage" />
                  <TimeBlock value={hours} label="Stunden" />
                  <TimeBlock value={minutes} label="Minuten" />
                  <TimeBlock value={seconds} label="Sekunden" />
                </div>
              )}

              {/* "In meinen Kalender eintragen" — Dropdown wurde entfernt (Mai 2026):
                  Die Calendar-URL enthielt Pascals Zoho-Registrierungs-Link,
                  weshalb Gäste sich versehentlich als „Pascal Schwab" eintrugen.
                  Falls wieder eingebaut: vorher den `uname`-Query-Param strippen. */}
            </div>
          </div>
        </section>

        {/* Direct Zoho registration CTA — sits right under the
            countdown so visitors who land on /webinar via a share link
            (and therefore haven't actually registered at Zoho yet) can
            jump straight to the Zoho form. Zoho dedupes by email, so
            existing registrants just see "already registered". */}
        <section className="mb-14 al-card overflow-hidden" data-testid="webinar-zoho-cta">
          <div className="p-6 sm:p-8 bg-gradient-to-br from-violet-50/70 via-white to-indigo-50/40">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md shadow-violet-200">
                <ExternalLink className="h-6 w-6 text-white" strokeWidth={2.2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="al-tag">Zoho-Registrierung</span>
                </div>
                <h2 className="font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight">
                  Noch nicht <span className="al-grad-text">bei Zoho registriert?</span>
                </h2>
                <p className="mt-1.5 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
                  Wir hosten den Call über <strong>Zoho Webinar</strong>. Du brauchst dort eine kostenlose 30-Sekunden-Registrierung — danach landet der persönliche Zugangs-Link direkt in deinem Postfach (plus automatische Reminder von Zoho).
                </p>
                <div className="mt-3 p-3 rounded-lg bg-violet-50/80 border border-violet-200 text-xs text-violet-900 leading-relaxed">
                  <strong>Warum nochmal Zoho?</strong> Nur registrierte Teilnehmer kriegen den Live-Link mit dem persönlichen Zugang. Falls du das schon gemacht hast — alles gut, Zoho sagt dir einfach „bereits angemeldet“.
                </div>
                {webinar?.zoho_link ? (
                  <a
                    href={webinar.zoho_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="webinar-zoho-register"
                    className="al-cta zoho-cta-pulse mt-6 w-full sm:w-auto justify-center text-base sm:text-lg !px-7 sm:!px-10 !py-4 sm:!py-5 font-display font-bold tracking-tight"
                  >
                    <ExternalLink className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.4} />
                    Jetzt bei Zoho registrieren
                  </a>
                ) : (
                  <span className="mt-5 inline-block text-sm text-slate-400 italic">
                    Registrierungs-Link folgt sobald der Termin steht.
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* What to expect — narrative block */}
        <section className="mb-14 al-card p-7 sm:p-10 bg-gradient-to-br from-white to-violet-50/40" data-testid="webinar-expect">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[var(--al-purple)]" />
            <span className="al-tag">Was dich im Webinar erwartet</span>
          </div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight tracking-tight mb-5">
            {isTech ? (
              <>Ein Blick <span className="al-grad-text">unter die Haube</span> — Technik, Setup, Q&amp;A.</>
            ) : (
              <>Eine <span className="al-grad-text">ehrliche Stunde</span> — ohne Hype, ohne Verkaufsdruck.</>
            )}
          </h2>
          <div className="space-y-4 text-[15px] sm:text-base text-slate-700 leading-relaxed">
            {isTech ? (
              <>
                <p>
                  Im Technik-Call zeigt dir <strong className="text-slate-900">Adrian Bienz</strong> (Trader &amp; Tech-Profi) live,
                  <strong className="text-slate-900"> wie das Alphalane-System unter der Haube arbeitet</strong> — Architektur,
                  Ausführung, Broker-Anbindung und das Setup deines eigenen Kontos.
                </p>
                <p>
                  Der Call endet mit einer <strong className="text-slate-900">offenen Q&amp;A-Runde</strong> — perfekt für alle
                  technischen Detailfragen, die im Live-Webinar zu kurz kommen.
                </p>
              </>
            ) : (
              <>
                <p>
                  Im Live-Webinar zeigen wir dir, <strong className="text-slate-900">wie Alphalane wirklich funktioniert</strong> —
                  die Technik dahinter, die Strategie, die Zahlen.
                </p>
                <p>
                  Der Call endet mit einer <strong className="text-slate-900">offenen Q&amp;A-Runde</strong>. Dort beantworten wir
                  <em> jede</em> Frage — auch unbequeme. Bring deine kritischen Punkte mit, wir freuen uns drauf.
                </p>
              </>
            )}
          </div>

          <div className="mt-7 grid sm:grid-cols-3 gap-3">
            {isTech ? (
              <>
                <Highlight icon={Brain} text="System-Architektur & Ausführung — live erklärt" />
                <Highlight icon={ShieldCheck} text="Konto-Setup & Broker-Anbindung Schritt für Schritt" />
                <Highlight icon={MessageSquare} text="Technik-Q&A mit Adrian — frag alles" />
              </>
            ) : (
              <>
                <Highlight icon={Brain} text="So funktioniert das System — die 4 Engines im Detail" />
                <Highlight icon={ShieldCheck} text="Risiko-Management: hard-coded Limits statt schöner Worte" />
                <Highlight icon={MessageSquare} text="Q&A — frag alles, auch das Unbequeme" />
              </>
            )}
          </div>

          <p className="mt-7 text-sm text-slate-500 italic">
            Tipp: Stell dir vorher schon Fragen zusammen — die <strong>besten Insights</strong> entstehen
            im Webinar genau dann, wenn jemand etwas fragt, das wir sonst nicht angesprochen hätten.
          </p>
        </section>

        {/* WhatsApp Reminder Opt-in */}
        <section
          id="reminder"
          className="mb-14 al-card overflow-hidden scroll-mt-24"
          data-testid="webinar-whatsapp-reminder"
        >
          <div className="p-6 sm:p-8 bg-gradient-to-br from-emerald-50/80 via-white to-violet-50/40">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md shadow-emerald-200">
                <Bell className="h-6 w-6 text-white" strokeWidth={2.2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="al-tag" style={{ background: "rgba(16,185,129,0.10)", color: "#047857", borderColor: "rgba(16,185,129,0.20)" }}>
                    WhatsApp-Reminder
                  </span>
                </div>
                <h2 className="font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight">
                  Erinnere mich <span className="al-grad-text">1 Stunde vorher</span> per WhatsApp.
                </h2>
                <p className="mt-1.5 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
                  Du verpasst den Call garantiert nicht — eine kurze WhatsApp 60 Min. vor Start mit deinem persönlichen Zugangs-Link.
                </p>

                {waDone ? (
                  <div
                    data-testid="webinar-reminder-success"
                    className="mt-5 inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-display font-semibold"
                  >
                    <Check className="h-5 w-5" strokeWidth={3} />
                    Reminder aktiviert. Wir schicken dir 1 h vorher eine WhatsApp.
                  </div>
                ) : (
                  <form
                    onSubmit={submitReminder}
                    className="mt-5 grid sm:grid-cols-12 gap-2.5"
                    data-testid="webinar-reminder-form"
                  >
                    <input
                      type="text"
                      value={waName}
                      onChange={(e) => setWaName(e.target.value)}
                      placeholder="Vorname"
                      required
                      data-testid="webinar-reminder-name"
                      className="sm:col-span-4 px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 transition"
                    />
                    <div className="sm:col-span-5 flex">
                      <select
                        value={waCC}
                        onChange={(e) => setWaCC(e.target.value)}
                        data-testid="webinar-reminder-cc"
                        className="px-2.5 py-3 rounded-l-xl border border-r-0 border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 transition"
                      >
                        <option value="+41">🇨🇭 +41</option>
                        <option value="+49">🇩🇪 +49</option>
                        <option value="+43">🇦🇹 +43</option>
                        <option value="+33">🇫🇷 +33</option>
                        <option value="+39">🇮🇹 +39</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+1">🇺🇸 +1</option>
                      </select>
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={waPhone}
                        onChange={(e) => setWaPhone(e.target.value.replace(/[^\d\s()-]/g, ""))}
                        placeholder="79 950 16 46"
                        required
                        data-testid="webinar-reminder-phone"
                        className="flex-1 px-4 py-3 rounded-r-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 transition"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={waSubmitting}
                      data-testid="webinar-reminder-submit"
                      className="sm:col-span-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-display font-bold text-sm transition-colors shadow-md shadow-emerald-200"
                    >
                      {waSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Sende…
                        </>
                      ) : (
                        <>
                          <Bell className="h-4 w-4" /> Reminder aktivieren
                        </>
                      )}
                    </button>
                    {waError && (
                      <div
                        data-testid="webinar-reminder-error"
                        className="sm:col-span-12 text-sm text-red-600 mt-1"
                      >
                        {waError}
                      </div>
                    )}
                    <p className="sm:col-span-12 text-[11px] text-slate-400 leading-relaxed mt-1">
                      Wir nutzen deine Nummer ausschliesslich für diesen einen Reminder. Kein Abo, kein Spam.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Refer a friend — share widget */}
        <section
          className="mb-14 al-card overflow-hidden"
          data-testid="webinar-share-widget"
        >
          <div className="p-6 sm:p-8 bg-gradient-to-br from-violet-50/60 via-white to-indigo-50/30">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-md shadow-violet-200">
                <Share2 className="h-6 w-6 text-white" strokeWidth={2.2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="al-tag">Empfehlung</span>
                </div>
                <h2 className="font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight">
                  Bring jemanden mit, der das <span className="al-grad-text">spannend findet.</span>
                </h2>
                <p className="mt-1.5 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
                  Wir haben Alphalane über persönliche Empfehlungen aufgebaut — kennst du jemanden, dem das gefallen würde? Ein Klick:
                </p>

                {/* Pre-filled message preview */}
                <div className="mt-4 px-4 py-3 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 italic leading-relaxed">
                  „{shareIntroDe}{" "}
                  <span className="text-[var(--al-purple)] font-medium not-italic break-all">{SHARE_URL}</span>“
                </div>

                {/* Share buttons */}
                <div className="mt-4 flex flex-wrap gap-2.5">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(shareTextDe)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="webinar-share-whatsapp"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366] hover:bg-[#1FB855] text-white font-display font-bold text-sm transition-colors shadow-md shadow-emerald-200"
                  >
                    <MessageSquare className="h-4 w-4" /> WhatsApp
                  </a>
                  <a
                    href={`https://t.me/share/url?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(shareIntroDe)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="webinar-share-telegram"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#229ED9] hover:bg-[#1B82B5] text-white font-display font-bold text-sm transition-colors shadow-md shadow-sky-200"
                  >
                    <Send className="h-4 w-4" /> Telegram
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(shareSubject)}&body=${encodeURIComponent(shareTextDe)}`}
                    data-testid="webinar-share-mail"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-display font-bold text-sm transition-colors shadow-md shadow-slate-200"
                  >
                    <MailIcon className="h-4 w-4" /> Mail
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    data-testid="webinar-share-copy"
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border font-display font-bold text-sm transition-colors ${
                      copied
                        ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                        : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                    }`}
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" strokeWidth={3} /> Kopiert
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" /> Link kopieren
                      </>
                    )}
                  </button>
                  {canNativeShare && (
                    <button
                      type="button"
                      onClick={handleNativeShare}
                      data-testid="webinar-share-native"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 font-display font-bold text-sm transition-colors sm:hidden"
                    >
                      <Share2 className="h-4 w-4" /> Mehr…
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Zoho Webinar App Download */}
        <section
          className="mb-14 al-card p-6 sm:p-8 bg-gradient-to-br from-white to-violet-50/40"
          data-testid="webinar-app-download"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Download className="h-4 w-4 text-[var(--al-purple)]" />
                <span className="al-tag">Mobil teilnehmen</span>
              </div>
              <h2 className="font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight">
                Lade die <span className="al-grad-text">Zoho Webinar</span> App
              </h2>
              <p className="mt-1.5 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
                Damit du {dateShort ? `am ${dateShort}` : "am Tag X"} sofort dabei bist — installiere die App jetzt schon.
                Alternativ kannst du am Computer direkt über den Browser teilnehmen.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2.5 flex-shrink-0">
              <a
                href={ZOHO_APP_IOS}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="webinar-app-ios"
                className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white transition-colors shadow-lg shadow-slate-900/10"
              >
                <Apple className="h-7 w-7" strokeWidth={1.5} />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                    Download im
                  </div>
                  <div className="font-display font-bold text-base">App Store</div>
                </div>
              </a>
              <a
                href={ZOHO_APP_ANDROID}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="webinar-app-android"
                className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white transition-colors shadow-lg shadow-slate-900/10"
              >
                <Smartphone className="h-7 w-7" strokeWidth={1.5} />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                    Jetzt bei
                  </div>
                  <div className="font-display font-bold text-base">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Agenda */}
        <section className="mb-14 al-card p-6 sm:p-8" data-testid="webinar-agenda">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-[var(--al-purple)]" />
            <h2 className="font-display font-bold text-lg">Agenda — 60 Minuten</h2>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            Wir starten pünktlich um 20:00 — wir empfehlen, 5 Minuten früher dazuzukommen.
          </p>
          <ol className="space-y-2">
            {AGENDA.map((a, i) => (
              <li
                key={`agenda-${a.time}`}
                data-testid={`agenda-${i}`}
                className="flex items-start gap-4 p-3 rounded-xl hover:bg-violet-50/40 transition-colors"
              >
                <div className="font-mono-tab text-sm font-bold text-[var(--al-purple)] bg-violet-50 rounded-md px-2.5 py-1.5 flex-shrink-0">
                  {a.time}
                </div>
                <div className="text-sm sm:text-[15px] text-slate-800 leading-snug pt-1">
                  {a.item}
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* What you'll learn */}
        <section className="mb-14" data-testid="webinar-facts">
          <div className="text-center mb-8">
            <span className="al-tag justify-self-center">
              <FileText className="h-3 w-3" /> Was dich erwartet
            </span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight tracking-tight">
              6 Fakten über Alphalane.
            </h2>
            <p className="mt-3 text-slate-600 max-w-xl mx-auto">
              Damit du gut vorbereitet ins Webinar startest — die wichtigsten Eckpunkte vorab.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {FACTS.map((f, i) => (
              <div
                key={`fact-${f.title}`}
                data-testid={`fact-${i}`}
                className="al-card p-5 sm:p-6 hover:border-violet-200 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 h-11 w-11 rounded-xl al-grad-bg flex items-center justify-center shadow-md shadow-violet-200">
                    <f.icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display font-bold text-base sm:text-lg leading-tight">
                      {f.title}
                    </h3>
                    <p className="mt-1.5 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
                      {f.text}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Final tip / next steps */}
        <section className="al-card p-6 sm:p-8 bg-gradient-to-br from-violet-50/80 to-white border-violet-200" data-testid="webinar-next-steps">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 h-12 w-12 rounded-xl al-grad-bg flex items-center justify-center">
              <Award className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-display font-bold text-xl mb-2">
                Bis dahin: Stell uns deine Fragen.
              </h2>
              <p className="text-sm sm:text-[15px] text-slate-700 leading-relaxed mb-4">
                Wir bauen die Q&A-Sektion am Ende des Webinars genau auf das auf, was du wissen willst.
                Schreib uns deine Fragen direkt per WhatsApp — wir lesen jede einzelne und
                priorisieren die häufigsten {dateShort ? `am ${dateShort}` : "im Webinar"} live.
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="https://wa.me/41799501646"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="webinar-wa-link"
                  className="al-cta text-sm"
                >
                  <MessageSquare className="h-4 w-4" /> Frage per WhatsApp stellen
                </a>
                <a
                  href="/insider-report.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="webinar-insider-link"
                  className="al-cta-ghost text-sm"
                >
                  <FileText className="h-4 w-4" /> Insider-Report herunterladen
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[var(--al-line)] mt-12">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 text-[11px] text-slate-400 flex flex-wrap gap-4 items-center justify-between">
          <div>© Alphalane Trading Systems · Moonshine Solutions GmbH</div>
          <div className="flex gap-4">
            <Link to="/impressum" className="hover:text-slate-700">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-slate-700">Datenschutz</Link>
            <Link to="/agb" className="hover:text-slate-700">AGB</Link>
          </div>
        </div>
      </footer>
      <QAStickyButton />
    </div>
  );
}

const TimeBlock = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="w-full bg-white/95 text-slate-900 rounded-2xl py-4 sm:py-6 px-1 font-mono-tab font-bold text-3xl sm:text-5xl tracking-tight shadow-xl">
      {String(value).padStart(2, "0")}
    </div>
    <div className="mt-2 text-[10px] sm:text-xs uppercase tracking-widest font-bold text-violet-200">
      {label}
    </div>
  </div>
);

const Highlight = ({ icon: Icon, text }) => (
  <div className="flex items-start gap-2.5 p-3 rounded-xl bg-white border border-violet-100">
    <div className="flex-shrink-0 h-8 w-8 rounded-lg al-grad-bg flex items-center justify-center">
      <Icon className="h-4 w-4 text-white" />
    </div>
    <p className="text-sm font-display font-semibold text-slate-800 leading-snug pt-0.5">
      {text}
    </p>
  </div>
);
